<template>
  <div class="form-filter">
    <div class="form-block">
      <div class="flex some-inputs">
        <div class="flex flex-col m-r-8 input-container">
          <label class="small-label">Название магазина*</label>
          <SceletonInput :is-loading="isLoadingData">
            <InputText
              placeholder="Введите значение"
              v-model="shop.shop_name"
              :disabled="false"
              :invalid="isInvalidForm && shop.shop_name == ''"
            />
          </SceletonInput>
        </div>
        <div class="flex flex-col input-container">
          <label class="small-label">Короткое название</label>
          <SceletonInput :is-loading="isLoadingData">
            <InputText
              placeholder="Введите значение"
              v-model="shop.shop_short_name"
              :disabled="false"
            />
          </SceletonInput>
        </div>
      </div>

      <div class="flex some-inputs m-t-8">
        <div class="flex flex-col m-r-8 input-container">
          <label class="small-label">ИНН*</label>
          <SceletonInput :is-loading="isLoadingData">
            <InputText
              placeholder="Введите значение"
              v-model="shop.shop_inn"
              :disabled="false"
              :invalid="isInvalidForm && shop.shop_inn == ''"
            />
          </SceletonInput>
        </div>
        <div class="flex flex-col input-container">
          <label class="small-label">Хэш ИНН</label>
          <SceletonInput :is-loading="isLoadingData">
            <InputText
              placeholder="Введите значение"
              v-model="shop.shop_pk"
              :disabled="false"
            />
          </SceletonInput>
        </div>
      </div>
    </div>

    <div class="form-block">
      <div class="flex flex-col">
        <label class="small-label">Категория*</label>
        <SceletonInput :is-loading="isLoadingData">
          <Dropdown
            :loading="isLoadingDataCategories"
            v-model="shop.category_id"
            optionLabel="name"
            option-value="id"
            :options="catsList"
            :invalid="isInvalidForm && shop.category_id == 0"
            placeholder="Выберите значение"
            filter
          ></Dropdown>
        </SceletonInput>
      </div>
      <div class="toggle-row m-t-8">
        <label for="has_data_from_providers" class="small-label"
          >Поступают данные от провайдеров</label
        >
        <SceletonInput :is-loading="isLoadingData">
          <InputSwitch
            v-model="shop.has_data_from_providers"
            inputId="has_data_from_providers"
          />
        </SceletonInput>
      </div>
    </div>
    <ImportantChangesDialog
      object-name="магазин"
      :visible="confirmVisible"
      @close="confirmVisible = false"
      @edit="editShop"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useShopsStore } from "@/store/dictionaries/shops";

import InputText from "primevue/inputtext";
import { areShopFieldsValid } from "@/services/dictionaries/shops";
import Dropdown from "primevue/dropdown";
import InputSwitch from "primevue/inputswitch";
import SceletonInput from "@/shared/forms/SceletonInput.vue";
import ImportantChangesDialog from "@/shared/dialogs/ImportantChangesDialog.vue";

import { rootGetters } from "@/main";
import { useCategoriesStore } from "@/store/catalog_data/categories";

const emit = defineEmits(["setLoading"]);
const route = useRoute();
const router = useRouter();
const store = useShopsStore();
const categoriesStore = useCategoriesStore();

const shop = ref({
  shop_name: "",
  shop_short_name: "",
  shop_pk: "",
  shop_inn: "",
  category_id: 0,
  has_data_from_providers: false,
});

const isInvalidForm = ref(false);
const isShopSetted = computed(() => {
  return Boolean(route.params.id);
});
const isLoadingData = ref(false);
const isLoadingDataCategories = ref(false);
const confirmVisible = ref(false);
const catsList = ref([]);

const reload = () => {
  isLoadingDataCategories.value = true;
  categoriesStore.getC1Categories().then((cats) => {
    catsList.value = cats;
    isLoadingDataCategories.value = false;
  });
  if (isShopSetted.value) {
    const shopId = parseInt(route.params.id);
    isLoadingData.value = true;
    store
      .getShop(shopId)
      .then((shopClone) => {
        if (shopClone) {
          shop.value.shop_name = shopClone.shop_name;
          shop.value.shop_short_name = shopClone.short_name;
          shop.value.shop_inn = shopClone.shop_inn;
          shop.value.shop_pk = shopClone.shop_pk;
          shop.value.category_id = shopClone.category_id;
          shop.value.has_data_from_providers =
            shopClone.has_data_from_providers ?? false;
        }
      })
      .finally(() => {
        isLoadingData.value = false;
      });
  }
};

onMounted(() => {
  reload();
});

const createShop = () => {
  if (!areShopFieldsValid(shop.value)) {
    isInvalidForm.value = true;
    rootGetters.toast.show(
      "warn",
      "Ошибка сохранения",
      `Проверьте, что все необходимые поля заполнены.`,
    );
    return;
  }

  const shopCopy = { ...shop.value };
  if (shop.value.shop_pk == "") {
    delete (shopCopy as any).shop_pk;
  }
  if (!isShopSetted.value) {
    performShopAction(() => store.createShop(shopCopy));
  } else {
    confirmVisible.value = true;
  }
};

const editShop = () => {
  confirmVisible.value = false;
  const shopCopy = { ...shop.value };
  if (shop.value.shop_pk == "") {
    delete (shopCopy as any).shop_pk;
  }
  performShopAction(() =>
    store.updateShop(shopCopy, parseInt(route.params.id)),
  );
};

const performShopAction = (action: any) => {
  emit("setLoading", true);
  const handleSuccess = () => {
    router.push({ name: "shops" });
    setTimeout(() => {
      store.activeFirst = false;
    }, 5000);
  };

  action()
    .then(handleSuccess)
    .catch((err: any) => {
      if (err.response && err.response.status == 409)
        rootGetters.toast.show("warn", "Ошибка", err.response.data.detail);
      if (err.response && err.response.status == 403)
        rootGetters.toast.show("warn", "Ошибка", "Нет доступа.");
    })
    .finally(() => {
      emit("setLoading", false);
    });
};

defineExpose({
  createShop,
});
</script>

<style lang="scss" scoped>
.form-filter {
  max-width: 100%;
  width: 100%;
  padding-bottom: 40px;
  min-width: 500px;
}

.form-block-title {
  margin-bottom: 8px;
}

.form-block:hover .group-button-container.hidden.dep {
  visibility: visible;
}

.role-container:hover .group-button-container.hidden.role {
  display: flex;
}

.small-label {
  color: #a19cab;
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.43rem;
  padding-bottom: 4px;
  padding-left: 4px;
}

.group-button-container.dep.hidden {
  visibility: hidden;
}

.group-button-container.role.hidden {
  display: none;
}

.delete-button {
  border-radius: 5px;
  background: #f7f7fd;
  width: 32px;
  height: 32px;
  padding-left: 9px;
  cursor: pointer;

  &:hover {
    background-color: #fafaff;
  }
}

.add-button {
  background-color: #dbdaf9;
  border-radius: 5px;
  padding: 3px 9px;
  cursor: pointer;
  height: 32px;
  margin-left: 12px;

  &:hover {
    background-color: #d4d3f8;

    outline-color: transparent;
  }
}

.f-s-12 {
  font-size: 12px;
}

.sub-block {
  padding: 12px;
  background-color: #f7f7fd;
  border: 1px solid #d0d0d6;
  border-radius: 6px;
}

.some-inputs {
  max-width: 100%;
}

.input-container {
  max-width: calc(50% - 4px);
  width: calc(50% - 4px);
}

.toggle-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
</style>
